import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Loader2, Download, Layers, Shield } from "lucide-react";
import MainLayout from "../Layouts/main";
import { useAuth } from "../context/AuthContext";
import SubscribeConfirmModal from "./SubscribeConfirmModal";

export default function BlueprintSharePage() {
    const { t } = useTranslation();
    const { shareToken } = useParams();
    const { user, setShowAuthModal } = useAuth();
    const [showSubscribe, setShowSubscribe] = useState(false);
    const [blueprint, setBlueprint] = useState(null);
    const { call: fetchBlueprint, isLoading } = useBluelagoon({
        method: "getBlueprintByShareToken",
    });

    useEffect(() => {
        if (shareToken) {
            fetchBlueprint({ shareToken }).then((res) => setBlueprint(res?.data || null)).catch(() => {});
        }
    }, [shareToken]);

    const handleSubscribe = () => {
        if (!user) { setShowAuthModal(true); return; }
        setShowSubscribe(true);
    };

    const content = isLoading ? (
        <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
    ) : !blueprint ? (
        <div className="flex items-center justify-center py-24">
            <p className="text-muted-foreground">{t("blueprints.blueprintNotFound")}</p>
        </div>
    ) : (
        <BlueprintShareContent
            blueprint={blueprint}
            changelog={Array.isArray(blueprint.changelog) ? blueprint.changelog : []}
            onSubscribe={handleSubscribe}
            showSubscribe={showSubscribe}
            setShowSubscribe={setShowSubscribe}
            shareToken={shareToken}
            t={t}
        />
    );

    return <MainLayout>{content}</MainLayout>;
}

function BlueprintShareContent({ blueprint, changelog, onSubscribe, showSubscribe, setShowSubscribe, shareToken, t }) {
    return (
        <div className="py-8 px-4">
            <div className="max-w-2xl mx-auto space-y-6">
                <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
                    {blueprint.imageUrl && (
                        <img src={blueprint.imageUrl} alt={blueprint.name} className="w-full h-48 object-cover rounded-lg" />
                    )}
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">{blueprint.name}</h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                {t("blueprints.createdBy")} {blueprint.creatorName}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Badge variant="secondary">v{blueprint.publishedVersion}</Badge>
                            {blueprint.badges?.includes("agentui_verified") && (
                                <Badge className="bg-blue-100 text-blue-700">
                                    <Shield className="w-3 h-3 mr-1" /> {t("blueprints.verified")}
                                </Badge>
                            )}
                        </div>
                    </div>
                    {blueprint.description && <p className="text-gray-600">{blueprint.description}</p>}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <Download className="w-4 h-4" /> {blueprint.downloadCount} {t("blueprints.downloads")}
                        </span>
                        <span className="flex items-center gap-1">
                            <Layers className="w-4 h-4" /> {blueprint.type}
                        </span>
                    </div>
                    {blueprint.integrationTypes?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {blueprint.integrationTypes.map((type) => (
                                <Badge key={type} variant="outline">{type}</Badge>
                            ))}
                        </div>
                    )}
                    <Button onClick={onSubscribe} className="w-full" size="lg">
                        {t("subscriptions.subscribe")}
                    </Button>
                </div>
                {changelog.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <h2 className="font-semibold mb-3">{t("blueprints.publish.changelog")}</h2>
                        <div className="space-y-2">
                            {changelog.slice(-5).reverse().map((entry, i) => (
                                <div key={i} className="flex justify-between text-sm border-b pb-2 last:border-0">
                                    <span>{entry.message}</span>
                                    <span className="text-muted-foreground">
                                        v{entry.version} - {new Date(entry.date).toLocaleDateString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <SubscribeConfirmModal
                open={showSubscribe}
                onOpenChange={setShowSubscribe}
                blueprint={blueprint}
                shareToken={shareToken}
            />
        </div>
    );
}
